// One file box for every file picker, HHL Ops and the vendor portal alike (Mitch, 2026-11-04: "missing the same shared
// drag/drop file picker. This needs to be unified for any file picker module"): drop files on it, paste them (a
// screenshot too) or click it to choose; it lights up while files are over it. Three pieces, never a bare
// <input type="file"> of a page's own:
// - `FileDrop`: the box with its behaviour, and the files chosen but not sent yet under it (a form's invoice, a paper PO's PI).
// - `useFileDrop()`: the behaviour alone, for a drop target bigger than the box (a dialog's pane, a record's files with
//   their table, a photo tile): spread `zone` on it, render `picker`, call `choose()`; `anywhere` catches a drop
//   anywhere on the page while it's mounted (a dialog), so the browser never opens the file over a half-filled form.
// - `DropFace`: the box's look, given `over` and what a click does.
import { FileUp, Loader2, X } from "lucide-react";
import { type ClipboardEvent, type DragEvent, type ReactNode, type Ref, useEffect, useRef, useState } from "react";
import { PLAN_TIME_ZONE, planToday } from "../../../shared/day.ts";
import { cx } from "./controls.tsx";
import { toast } from "./toast.tsx";

/** Whether a drag carries files (not text or a link being dragged). */
export const hasFiles = (e: { dataTransfer: DataTransfer | null }) => !!e.dataTransfer && [...e.dataTransfer.types].includes("Files");

/** Whether `accept` (".pdf,image/*,application/pdf", as an input takes it) takes a file: its extension, its type or its family. */
export function accepts(file: File, accept?: string): boolean {
  if (!accept) return true;
  const name = file.name.toLowerCase();
  const type = (file.type || "").toLowerCase();
  return accept
    .split(",")
    .map((a) => a.trim().toLowerCase())
    .filter(Boolean)
    .some((a) => (a.startsWith(".") ? name.endsWith(a) : a.endsWith("/*") ? type.startsWith(a.slice(0, -1)) : type === a));
}

/** "2026-10-28 18:05" at HHL (Texas), for a pasted screenshot's name: never the UTC time, hours ahead. */
const pastedAt = () => {
  const now = new Date();
  const time = new Intl.DateTimeFormat("en-GB", { timeZone: PLAN_TIME_ZONE, hour: "2-digit", minute: "2-digit", hour12: false }).format(now);
  return `${planToday(now)} ${time}`;
};

/** A pasted screenshot (the clipboard calls it "image.png") named for when it was pasted, "Pasted 2026-11-04 14:05.png", so a few read apart. */
export const pastedName = (f: File) =>
  f.name && f.name !== "image.png" ? f : new File([f], `Pasted ${pastedAt()}.${f.type.split("/")[1] || "png"}`, { type: f.type });

const typing = (t: EventTarget | null) => {
  const el = t as HTMLElement | null;
  return !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable);
};

/** The box's words; the portal passes its own (English or 中文). */
export type DropText = { how: string; over: string; remove: string; refused: (names: string) => string };
export const DROP_TEXT: DropText = {
  how: "Drop files here, paste a screenshot, or click to choose",
  over: "Drop to add",
  remove: "Leave it out",
  refused: (names) => `This box doesn't take ${names}`,
};

export function useFileDrop({
  onFiles,
  accept,
  multiple = true,
  disabled,
  capture,
  anywhere,
  text = DROP_TEXT,
}: {
  onFiles: (files: File[]) => void;
  /** What it takes, as an input's `accept`; a dropped file it doesn't take is refused with a toast. */
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  /** A phone's or tablet's camera ("environment": the back one). */
  capture?: "environment" | "user";
  /** While mounted, files dropped or pasted (outside a text box) anywhere on the page come here: a dialog's one box. */
  anywhere?: boolean;
  text?: DropText;
}) {
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const face = useRef<HTMLDivElement>(null);
  const take = (list: Iterable<File> | null | undefined) => {
    const all = [...(list ?? [])];
    const ok = all.filter((f) => accepts(f, accept));
    const refused = all.filter((f) => !ok.includes(f));
    if (refused.length) toast.error(text.refused(refused.map((f) => f.name).join(", ")));
    const files = multiple ? ok : ok.slice(0, 1);
    if (files.length) onFiles(files);
  };
  const latest = useRef(take);
  latest.current = take;
  useEffect(() => {
    if (!anywhere || disabled) return;
    const onOver = (e: globalThis.DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
      setOver((was) => {
        if (!was) face.current?.scrollIntoView({ block: "nearest" });
        return true;
      });
    };
    const onLeave = (e: globalThis.DragEvent) => {
      if (!e.relatedTarget) setOver(false); // left the window
    };
    const onDrop = (e: globalThis.DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      setOver(false);
      latest.current(e.dataTransfer?.files);
    };
    const onPaste = (e: globalThis.ClipboardEvent) => {
      const files = [...(e.clipboardData?.files ?? [])];
      if (!files.length || typing(e.target)) return;
      e.preventDefault();
      latest.current(files.map(pastedName));
    };
    window.addEventListener("dragover", onOver);
    window.addEventListener("dragleave", onLeave);
    window.addEventListener("drop", onDrop);
    window.addEventListener("paste", onPaste);
    return () => {
      window.removeEventListener("dragover", onOver);
      window.removeEventListener("dragleave", onLeave);
      window.removeEventListener("drop", onDrop);
      window.removeEventListener("paste", onPaste);
    };
  }, [anywhere, disabled]);
  const zone = {
    onDragOver: (e: DragEvent) => {
      if (disabled || !hasFiles(e)) return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = "copy";
      setOver(true);
    },
    onDragLeave: (e: DragEvent) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node)) setOver(false);
    },
    onDrop: (e: DragEvent) => {
      setOver(false);
      if (disabled || !e.dataTransfer.files.length) return;
      e.preventDefault();
      e.stopPropagation();
      take(e.dataTransfer.files);
    },
    onPaste: (e: ClipboardEvent) => {
      const files = [...e.clipboardData.files];
      if (disabled || !files.length || typing(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
      take(files.map(pastedName));
    },
  };
  const picker = (
    <input
      ref={input}
      type="file"
      hidden
      accept={accept}
      multiple={multiple}
      capture={capture}
      onChange={(e) => {
        take(e.target.files);
        e.target.value = "";
      }}
    />
  );
  return { over, zone, picker, face, take, choose: () => !disabled && input.current?.click() };
}

/** The box: what it's for, then how ("Drop files here, paste a screenshot, or click to choose"); a click chooses. */
export function DropFace({
  over,
  onClick,
  title,
  hint,
  size = "md",
  busy,
  disabled,
  extra,
  faceRef,
  text = DROP_TEXT,
  className,
}: {
  over: boolean;
  onClick: () => void;
  /** What goes in it ("The vendor's invoice", "Add more files"). */
  title?: ReactNode;
  /** A line under it (what it's kept for). */
  hint?: ReactNode;
  /** sm one line (a form's field), md a box, lg filling what it's in (a dialog's pane). */
  size?: "sm" | "md" | "lg";
  /** Shown instead while files are read or sent ("Reading…"). */
  busy?: ReactNode;
  disabled?: boolean;
  /** Other ways in beside the click (Choose a folder). */
  extra?: ReactNode;
  faceRef?: Ref<HTMLDivElement>;
  text?: DropText;
  className?: string;
}) {
  const lg = size === "lg";
  return (
    <div
      ref={faceRef}
      className={cx(
        "flex rounded-md border border-dashed transition-colors",
        lg ? "min-h-0 flex-1 flex-col items-stretch" : "flex-col items-stretch",
        over ? "border-accent bg-accent-soft text-accent-text" : "border-line-strong text-muted",
        disabled && "opacity-60",
        className,
      )}
    >
      <button
        type="button"
        disabled={disabled || !!busy}
        onClick={onClick}
        className={cx(
          "flex min-w-0 rounded-md text-[12px] hover:text-accent-text disabled:cursor-default",
          size === "sm" ? "items-center gap-2 px-3 py-2 text-left" : "flex-1 flex-col items-center justify-center gap-1 text-center",
          size === "md" && "px-3 py-3",
          lg && "gap-2 p-8",
        )}
      >
        {busy ? (
          <Loader2 className={cx("shrink-0 animate-spin", lg ? "size-7" : "size-4")} />
        ) : (
          <FileUp className={cx("shrink-0", lg ? "size-8 text-faint" : "size-4")} />
        )}
        {size === "sm" ? (
          <span className="min-w-0 truncate">
            {busy ?? (over ? text.over : title ? <span className="font-medium text-text">{title}</span> : text.how)}
            {!busy && !over && title && <span className="text-faint"> · {text.how}</span>}
          </span>
        ) : (
          <>
            {busy ? (
              <span>{busy}</span>
            ) : over ? (
              <span className="font-medium">{text.over}</span>
            ) : (
              <>
                {title && <span className={cx("font-medium text-text", lg && "text-[13px]")}>{title}</span>}
                {hint && <span className="max-w-[440px] text-muted">{hint}</span>}
                <span className={cx(title ? "text-faint" : "", lg && "text-accent-text underline")}>{text.how}</span>
              </>
            )}
          </>
        )}
      </button>
      {extra && <div className="flex justify-center gap-3 pb-2 text-[12px]">{extra}</div>}
    </div>
  );
}

const sizeText = (n: number) => (n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`);

/** Files chosen and not sent yet, each with its size and × (leave it out). */
export function ChosenFiles({ files, onRemove, text = DROP_TEXT }: { files: File[]; onRemove?: (index: number) => void; text?: DropText }) {
  if (!files.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {files.map((f, i) => (
        <span key={`${f.name}:${f.size}:${f.lastModified}`} className="inline-flex h-6 max-w-72 items-center gap-1.5 rounded bg-surface-2 px-2 text-[12px]">
          <span className="truncate" title={f.name}>
            {f.name}
          </span>
          <span className="shrink-0 text-faint">{sizeText(f.size)}</span>
          {onRemove && (
            <button type="button" title={text.remove} aria-label={text.remove} onClick={() => onRemove(i)} className="shrink-0 text-faint hover:text-danger">
              <X className="size-3" />
            </button>
          )}
        </span>
      ))}
    </div>
  );
}

/**
 * The file box with its behaviour: drop, paste or click to choose; `chosen` lists what's waiting under it (a form sends
 * them with the rest). `children` go above the box inside the drop target (a record's files table).
 */
export function FileDrop({
  onFiles,
  accept,
  multiple = true,
  disabled,
  busy,
  title,
  hint,
  size,
  chosen,
  onRemove,
  anywhere,
  capture,
  extra,
  text = DROP_TEXT,
  className,
  children,
}: {
  onFiles: (files: File[]) => void;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  busy?: ReactNode;
  title?: ReactNode;
  hint?: ReactNode;
  size?: "sm" | "md" | "lg";
  chosen?: File[];
  onRemove?: (index: number) => void;
  anywhere?: boolean;
  capture?: "environment" | "user";
  extra?: ReactNode;
  text?: DropText;
  className?: string;
  children?: ReactNode;
}) {
  const d = useFileDrop({ onFiles, accept, multiple, disabled, anywhere, capture, text });
  return (
    <div
      {...d.zone}
      tabIndex={disabled ? undefined : -1}
      className={cx("flex flex-col gap-1.5 rounded-md focus:outline-none", size === "lg" && "min-h-0 flex-1", className)}
    >
      {children}
      <DropFace
        over={d.over}
        onClick={d.choose}
        title={title}
        hint={hint}
        size={size}
        busy={busy}
        disabled={disabled}
        extra={extra}
        faceRef={d.face}
        text={text}
      />
      {chosen && <ChosenFiles files={chosen} onRemove={onRemove} text={text} />}
      {d.picker}
    </div>
  );
}
